import AuthCall from "./auth";
import { FilterPagination, ApiResponse, EndpointRequest } from "../types/base";
import { Testkit, TestkitFilterAPI, TestkitSorterAPI } from "../../types/testkit";
import { APICount } from "../../types/misc";

const TestkitAPI = {
  getKitByID: async ({ id, signal }: { id: string } & EndpointRequest) => await AuthCall.get({
    endpoint: `/secure/testkit/instances/${id}`,
    signal,
  }) as ApiResponse<Testkit>,

  getAllKits: async ({ signal, ...params }: TestkitFilterAPI & TestkitSorterAPI & FilterPagination & EndpointRequest) => await AuthCall.get({
    endpoint: '/secure/testkit/instances',
    params,
    signal,
  }) as ApiResponse<Testkit[]>,

  getKitCount: async ({ signal, ...params }: TestkitFilterAPI & EndpointRequest) => await AuthCall.get({
    endpoint: '/secure/testkit/instances/count',
    params,
    signal,
  }) as ApiResponse<APICount>,

  downloadAllKits: async ({ filename, signal, ...params }: { filename?: string } & TestkitFilterAPI & TestkitSorterAPI & FilterPagination & EndpointRequest) => await AuthCall.download({
    method: 'get',
    filename,
    endpoint: '/secure/testkit/instances',
    params: { ...params, format: 'csv' },
    signal,
  }),

  updateKitData: async ({
    testkit,
    siteId,
    paperLot, paperExpiry,
    swabLot, swabExpiry,
    additionalPaperLot, additionalPaperExpiry,
    additionalSwabLot, additionalSwabExpiry,
    signal,
  }: {
    testkit: Testkit,
    siteId?: string,
    paperLot?: string, paperExpiry?: string,
    swabLot?: string, swabExpiry?: string,
    additionalPaperLot?: string, additionalPaperExpiry?: string,
    additionalSwabLot?: string, additionalSwabExpiry?: string,
  } & EndpointRequest) => await AuthCall.put({
    endpoint: '/secure/testkit/instance',
    data: {
      ...testkit,
      site: siteId || testkit.site,
      paperLot:     paperLot    || testkit.paperLot,
      paperExpiry:  paperExpiry || testkit.paperExpiry,
      swabLot:      swabLot     || testkit.swabLot,
      swabExpiry:   swabExpiry  || testkit.swabExpiry,
      additionalPaperLot:     additionalPaperLot    || testkit.additionalPaperLot,
      additionalPaperExpiry:  additionalPaperExpiry || testkit.additionalPaperExpiry,
      additionalSwabLot:      additionalSwabLot     || testkit.additionalSwabLot,
      additionalSwabExpiry:   additionalSwabExpiry  || testkit.additionalSwabExpiry,
    },
    signal,
  }) as ApiResponse<Testkit>,

  discardKit: async ({ testkit, signal }: { testkit: Testkit } & EndpointRequest) => await AuthCall.post({
    endpoint: `/secure/testkit/discard/${testkit.id}`,
    signal,
  }) as ApiResponse<Testkit>,

  generateNewKits: async ({ count, signal }: { count: number } & EndpointRequest) => await AuthCall.post({
    endpoint: `/secure/testkit/new/${count}`,
    signal,
  }),

  generateNewKitsSubAssembly: async ({ count, manufacturer, signal }: { count: number, manufacturer: string } & EndpointRequest) => await AuthCall.post({
    endpoint: `/secure/testkit/sub-assembly/${count}`,
    params: { manufacturer },
    signal,
  }),

  uploadStockKitsCSV: async ({ file, signal }: { file: File } & EndpointRequest) => {
    const formData = new FormData();
    formData.append('file', file);

    return await AuthCall.post({
      endpoint: '/secure/testkit/stock/upload',
      data: formData,
      options: { 'Content-Type': 'multipart/form-data' },
      signal,
    })
  },

  uploadDispatchedKitsCSV: async ({ file, signal }: { file: File } & EndpointRequest) => {
    const formData = new FormData();
    formData.append('file', file);

    return await AuthCall.post({
      endpoint: '/secure/testkit/dispatched/upload',
      data: formData,
      options: { 'Content-Type': 'multipart/form-data' },
      signal,
    })
  },

  allocateKitsToSite: async ({ count, siteId, signal }: { count: number, siteId?: string } & EndpointRequest) => await AuthCall.post({
    endpoint: `/secure/testkit/allocate/${count}`,
    params: { siteId },
    signal,
  }),
};

export default TestkitAPI;
